import React from 'react'

export type DraggedContentParams = {
  id: string
  momentId: string
  /** Parent moment's scheduled_at as ISO string. Used for prevent-drop. */
  momentScheduledAt: string | null
  /** Parent moment's scheduled_end_at as ISO string, or null if single-day. */
  momentScheduledEndAt: string | null
  /**
   * Content's own scheduled_at as ISO string (or null if unscheduled).
   * Preserved at drag-start so `ContentStack.onDrop` can combine the
   * target day with the post's existing time-of-day — without this, a
   * 3:00 PM post dragged to a new day would silently become midnight.
   */
  scheduledAt: string | null
  /** Lets span-mode drops preserve bar duration via a shared delta. */
  takedownAt: string | null
  contentPosition: number
}

type ContentDragContextValue = {
  draggedContent: DraggedContentParams | null
  setDraggedContent: React.Dispatch<
    React.SetStateAction<DraggedContentParams | null>
  >
  /**
   * Publishes the YYYY-MM-DD day key of the currently-hovered column (or
   * null to clear). Backed by an external store, not React state, so a
   * dragover-driven change re-renders only the two `WeekViewDayHeader`
   * components transitioning hover state — not every consumer of this
   * context. Stable identity across renders.
   */
  setHoveredDate: (date: string | null) => void
}

type HoveredDateStore = {
  get: () => string | null
  subscribe: (callback: () => void) => () => void
}

export const ContentDragContext = React.createContext<
  ContentDragContextValue | undefined
>(undefined)

const HoveredDateStoreContext = React.createContext<
  HoveredDateStore | undefined
>(undefined)

/**
 * Tracks the currently-dragged content item in Content mode. Parallel to
 * `MomentDragContext`. Consumed by `WeekViewDayHeader` to decide whether a
 * day cell is a valid drop target (within the parent moment's range).
 *
 * The hovered-date signal is intentionally NOT in React state: dragover
 * fires per pointer move, and a top-level state update would re-render the
 * entire calendar subtree. We expose it as an external store so only the
 * `useIsHoveredDate(date)` subscribers (the day headers) re-render — and
 * only when their own date enters or leaves the hover window.
 */
export const ContentDragContextProvider = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const [draggedContent, setDraggedContent] =
    React.useState<DraggedContentParams | null>(null)

  const hoveredDateRef = React.useRef<string | null>(null)
  const subscribersRef = React.useRef<Set<() => void>>(new Set())

  const store = React.useMemo<HoveredDateStore>(
    () => ({
      get: () => hoveredDateRef.current,
      subscribe: (callback) => {
        subscribersRef.current.add(callback)
        return () => {
          subscribersRef.current.delete(callback)
        }
      },
    }),
    []
  )

  const setHoveredDate = React.useCallback((date: string | null) => {
    if (hoveredDateRef.current === date) return
    hoveredDateRef.current = date
    subscribersRef.current.forEach((callback) => callback())
  }, [])

  const value = React.useMemo(
    () => ({ draggedContent, setDraggedContent, setHoveredDate }),
    [draggedContent, setHoveredDate]
  )

  return (
    <ContentDragContext.Provider value={value}>
      <HoveredDateStoreContext.Provider value={store}>
        {children}
      </HoveredDateStoreContext.Provider>
    </ContentDragContext.Provider>
  )
}

export const useContentDragContext = (): ContentDragContextValue => {
  const context = React.useContext(ContentDragContext)
  if (context === undefined) {
    throw new Error(
      'useContentDragContext must be used inside a ContentDragContextProvider.'
    )
  }
  return context
}

/**
 * Subscribe to the hovered-date store with a per-date selector. The
 * component re-renders only when its own date crosses into or out of the
 * hovered column, not on every pointer move.
 */
export const useIsHoveredDate = (date: string): boolean => {
  const store = React.useContext(HoveredDateStoreContext)
  if (store === undefined) {
    throw new Error(
      'useIsHoveredDate must be used inside a ContentDragContextProvider.'
    )
  }
  const [isHovered, setIsHovered] = React.useState(() => store.get() === date)
  React.useEffect(() => {
    setIsHovered(store.get() === date)
    return store.subscribe(() => {
      setIsHovered(store.get() === date)
    })
  }, [date, store])
  return isHovered
}
